import {Component, Injectable} from '@angular/core';

@Injectable()
export class ZoomService {
  zoomLevel: number = 1; // Start-Zoomlevel (1 = Originalgröße)
  originX: number = 0; // Startwert für Transform-Origin X
  originY: number = 0; // Startwert für Transform-Origin Y
  offsetX: number = 0; // Verschiebung nach links/rechts
  offsetY: number = 0; // Verschiebung nach oben/unten
  private initialDistance: number | null = null; // Abstand zwischen den Fingern
  private lastCenter: { x: number; y: number } | null = null; // Zentrum der Pinch-Geste
  private isPanning: boolean = false; // Erkennt, ob aktuell Pan (Scroll) verwendet wird

  /**
   * Wird aufgerufen, wenn die Berührung startet
   */
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      // Abstand und Mittelpunkt der Berührungspunkte berechnen
      this.initialDistance = this.getDistance(event.touches[0], event.touches[1]);
      this.lastCenter = this.getCenter(event.touches[0], event.touches[1]);

      // Starte Transform-Origin basierend auf Pinch-Mittelpunkt
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      this.originX = this.lastCenter.x - rect.left;
      this.originY = this.lastCenter.y - rect.top;

      this.isPanning = false; // Kein Scroll
    } else if (event.touches.length === 1) {
      this.isPanning = true; // Beim Scrollen (1 Finger)
    }
  }

  /**
   * Wird beim Bewegen der Finger aufgerufen
   */
  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2 && this.initialDistance) {
      // Berechnet den aktuellen Abstand und Mittelpunkt
      const currentDistance = this.getDistance(event.touches[0], event.touches[1]);
      const currentCenter = this.getCenter(event.touches[0], event.touches[1]);

      // Zoom-Level aktualisieren
      const scaleChange = currentDistance / this.initialDistance;
      const newZoomLevel = Math.min(3, Math.max(1, this.zoomLevel * scaleChange)); // Begrenzung zwischen 1 und 3

      // Rücksetzen, wenn Zoom-Level = 1
      if (newZoomLevel === 1) {
        this.resetZoom();
      } else {
        // Verschiebung (Translate) dynamisch berechnen
        this.offsetX += (currentCenter.x - (this.lastCenter?.x || 0)) / this.zoomLevel;
        this.offsetY += (currentCenter.y - (this.lastCenter?.y || 0)) / this.zoomLevel;

        this.zoomLevel = newZoomLevel;
        this.lastCenter = currentCenter;
        this.initialDistance = currentDistance;
      }

      event.preventDefault(); // Standard-Verhalten (z. B. Scrollen) verhindern
    } else if (this.isPanning && event.touches.length === 1) {
      // Pan-Logik (1 Finger zum Scrollen)
      const deltaX = event.touches[0].clientX - this.lastCenter!.x;
      const deltaY = event.touches[0].clientY - this.lastCenter!.y;

      this.offsetX += deltaX;
      this.offsetY += deltaY;

      this.lastCenter = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
  }

  /**
   * Wird aufgerufen, wenn die Berührung beendet wird
   */
  onTouchEnd(event: TouchEvent) {
    if (event.touches.length < 2) {
      this.initialDistance = null;
      this.lastCenter = null;
      this.isPanning = false; // Zurücksetzen des Pan-Modus
    }
  }

  /**
   * Setzt den Zoom und die Verschiebung zurück
   */
  private resetZoom() {
    this.zoomLevel = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.originX = 0; // Mitte des Elements
    this.originY = 0; // Mitte des Elements
  }

  /**
   * Hilfsfunktion: Berechnet den Abstand zwischen zwei Berührungspunkten
   */
  private getDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Hilfsfunktion: Berechnet den Mittelpunkt zwischen zwei Berührungspunkten
   */
  private getCenter(touch1: Touch, touch2: Touch): { x: number; y: number } {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  }
}
